import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Download, Lock } from 'lucide-react';

import type { VisaoPorPilar } from '@engine/tipos.ts';
import { api, ErroApi } from '../lib/api.ts';
import { mesAnterior, numero, percentual, reais } from '../lib/formato.ts';
import { useAuth } from '../contexto/auth.tsx';
import { Aviso, Botao, Campo, Cartao } from '../componentes/ui.tsx';
import { TabelaResultado } from '../componentes/TabelaResultado.tsx';
import { TabelaComAjustes } from '../componentes/TabelaComAjustes.tsx';
import type { RegrasDeAjuste } from '@engine/ajustes.ts';
import { baixar, csvDoFechamento } from '../lib/exportar.ts';
import type { LinhaResultado } from '../lib/tipos.ts';

interface Previa {
  competencia: string;
  periodo: { inicio: string; fim: string };
  regras: { id: string; nome: string };
  regrasDeAjuste: RegrasDeAjuste;
  totalAgendamentos: number;
  visaoDoRelatorio: VisaoPorPilar;
  projecaoDoBalizador: { ativa: boolean; minimoDeDiasDePresenca: number };
  totaisPorVisao: Record<string, number>;
  resultado: {
    taxaOcupacao: number;
    mediaComparecimento: { agendado: number; atendido: number; taxa: number; qtdConsiderados: number };
    mediaBalizador: { media: number; qtdConsiderados: number };
    linhas: LinhaResultado[];
    totalGeral: number;
    avisos: string[];
  };
}

const NOME_DA_VISAO: Record<string, string> = {
  data_do_agendamento: 'data do agendamento',
  data_de_criacao: 'data de criação',
};

/** Qual data recortou cada pilar, e quanto cada recorte trouxe. */
function notaDaVisao(visao: VisaoPorPilar, totais: Record<string, number>): string {
  if (visao.comparecimento === visao.balizador) {
    return `recortado pela ${NOME_DA_VISAO[visao.comparecimento] ?? visao.comparecimento}`;
  }
  const parte = (v: string): string => `${NOME_DA_VISAO[v] ?? v} (${numero(totais[v] ?? 0)})`;
  return `comparecimento pela ${parte(visao.comparecimento)} · balizador pela ${parte(visao.balizador)}`;
}

export function Fechamento() {
  const { podeFechar } = useAuth();
  const padrao = mesAnterior();

  const [inicio, setInicio] = useState(padrao.inicio);
  const [fim, setFim] = useState(padrao.fim);
  const [taxa, setTaxa] = useState('');
  const [origem, setOrigem] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  // O total já com faltas e proporcionalidade, vindo da tabela de ajustes.
  const [totalAjustado, setTotalAjustado] = useState<number | null>(null);

  const previa = useMutation({
    onSuccess: () => setTotalAjustado(null),
    mutationFn: () =>
      api<Previa>('/fechamentos/previa', {
        metodo: 'POST',
        corpo: { periodoInicio: inicio, periodoFim: fim, taxaOcupacao: Number(taxa.replace(',', '.')) },
      }),
  });

  const fechar = useMutation({
    mutationFn: () =>
      api<{ id: string; competencia: string; totalGeral: number }>('/fechamentos', {
        metodo: 'POST',
        corpo: {
          periodoInicio: inicio,
          periodoFim: fim,
          taxaOcupacao: Number(taxa.replace(',', '.')),
          taxaOcupacaoOrigem: origem,
        },
      }),
    onSuccess: () => setConfirmando(false),
  });

  const r = previa.data?.resultado;

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Calcular fechamento"
        descricao="A prévia não grava nada. Confira os números antes de fechar o mês."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Campo rotulo="Período de" tipo="date" valor={inicio} onChange={setInicio} />
          <Campo rotulo="até" tipo="date" valor={fim} onChange={setFim} />
          <Campo
            rotulo="Taxa de ocupação"
            valor={taxa}
            onChange={setTaxa}
            dica="Do dashboard de ocupação das agendas. Aceita 74,6 ou 0,746."
          />
          <div className="flex items-end">
            <Botao onClick={() => previa.mutate()} carregando={previa.isPending}>
              Calcular prévia
            </Botao>
          </div>
        </div>

        {previa.isError && (
          <div className="mt-4">
            <Aviso tom="erro" titulo="Não foi possível calcular">
              <p>{previa.error.message}</p>
            </Aviso>
          </div>
        )}
      </Cartao>

      {r !== undefined && previa.data !== undefined && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Resumo
              rotulo="Total do fechamento"
              valor={reais(totalAjustado ?? r.totalGeral)}
              nota={
                totalAjustado !== null && Math.abs(totalAjustado - r.totalGeral) > 0.004
                  ? `${reais(r.totalGeral)} antes das faltas e da proporcionalidade`
                  : undefined
              }
              destaque
            />
            <Resumo rotulo="Pessoas" valor={String(r.linhas.length)} />
            <Resumo
              rotulo="Agendamentos lidos"
              valor={numero(previa.data.totalAgendamentos)}
              // Duas visões do relatório dão totais diferentes. Deixar isso à
              // vista evita a pergunta "por que o número mudou este mês".
              nota={notaDaVisao(previa.data.visaoDoRelatorio, previa.data.totaisPorVisao)}
            />
            <Resumo rotulo="Taxa de ocupação" valor={percentual(r.taxaOcupacao)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Resumo
              rotulo="Média da equipe (comparecimento)"
              valor={`${numero(r.mediaComparecimento.atendido)} / ${numero(r.mediaComparecimento.agendado)} = ${percentual(r.mediaComparecimento.taxa)}`}
              nota={`${r.mediaComparecimento.qtdConsiderados} atendentes acima do piso · base do Supervisor e do Backoffice`}
            />
            <Resumo
              rotulo="Média de produção (balizador)"
              valor={numero(r.mediaBalizador.media)}
              nota={`${r.mediaBalizador.qtdConsiderados} atendentes considerados`}
            />
          </div>

          {r.avisos.length > 0 && (
            <Aviso titulo={`${r.avisos.length} ponto(s) para conferir antes de fechar`}>
              {r.avisos.map((a) => (
                <p key={a}>· {a}</p>
              ))}
            </Aviso>
          )}

          <Cartao
            titulo={`Resultado por pessoa — ${previa.data.competencia}`}
            descricao={
              `Regras: ${previa.data.regras.nome}` +
              // Dito aqui porque "a projeção não funcionou" e "a projeção está
              // desligada nesta versão" eram o mesmo sintoma na tela.
              ` · projeção do balizador ${
                previa.data.projecaoDoBalizador?.ativa === true
                  ? `ligada (mínimo de ${previa.data.projecaoDoBalizador.minimoDeDiasDePresenca} dias)`
                  : 'desligada'
              }`
            }
            acoes={
              <div className="flex gap-2">
                <Botao
                  tipo="secundario"
                  onClick={() =>
                    baixar(
                      `previa-${previa.data?.competencia}.csv`,
                      csvDoFechamento(previa.data?.competencia ?? '', r.linhas),
                    )
                  }
                >
                  <Download className="size-4" />
                  Exportar planilha
                </Botao>
                {podeFechar && (
                  <Botao tipo="perigo" onClick={() => setConfirmando(true)}>
                    <Lock className="size-4" />
                    Fechar premiações
                  </Botao>
                )}
              </div>
            }
          >
            <TabelaResultado linhas={r.linhas} />
          </Cartao>

          <Cartao
            titulo="Faltas e dias trabalhados"
            descricao="Lance aqui antes de fechar. O valor muda enquanto você digita, e a gravação acontece ao sair do campo."
          >
            <TabelaComAjustes
              competencia={previa.data.competencia}
              linhas={r.linhas}
              regras={previa.data.regrasDeAjuste}
              podeEditar={podeFechar}
              aoMudarTotal={setTotalAjustado}
            />
          </Cartao>
        </>
      )}

      {confirmando && (
        <Cartao titulo="Fechar as premiações desta competência">
          <div className="space-y-4">
            <Aviso tom="atencao" titulo="Esta ação é definitiva">
              <p>
                O resultado será gravado exatamente como está agora, junto das regras usadas, e
                ficará vinculado à competência {previa.data?.competencia}.
              </p>
              <p>
                Depois disso <strong>não é possível calcular de novo</strong> sem reabrir o mês — e a
                reabertura fica registrada, com motivo e responsável.
              </p>
            </Aviso>

            <Campo
              rotulo="De onde veio a taxa de ocupação"
              valor={origem}
              onChange={setOrigem}
              dica="Ex.: Dashboard de ocupação das agendas, consulta de 25/08. Daqui a um ano é isso que explica o número."
              obrigatorio
            />

            {fechar.isError && (
              <Aviso tom="erro">
                <p>{fechar.error.message}</p>
                {(() => {
                  const detalhe =
                    fechar.error instanceof ErroApi
                      ? (fechar.error.detalhe as { fechamentoId?: string } | undefined)
                      : undefined;
                  return detalhe?.fechamentoId === undefined ? null : (
                    <p>
                      <Link
                        to={`/historico/${detalhe.fechamentoId}`}
                        className="font-medium underline hover:no-underline"
                      >
                        Abrir o fechamento existente
                      </Link>{' '}
                      — o botão de reabrir está lá.
                    </p>
                  );
                })()}
              </Aviso>
            )}

            {fechar.isSuccess ? (
              <Aviso tom="info" titulo="Fechamento gravado">
                <p>
                  {fechar.data.competencia} fechado com total de {reais(fechar.data.totalGeral)}. Está
                  no histórico.
                </p>
              </Aviso>
            ) : (
              <div className="flex gap-3">
                <Botao
                  tipo="perigo"
                  onClick={() => fechar.mutate()}
                  carregando={fechar.isPending}
                  desabilitado={origem.trim() === ''}
                >
                  Confirmar e fechar
                </Botao>
                <Botao tipo="secundario" onClick={() => setConfirmando(false)}>
                  Cancelar
                </Botao>
              </div>
            )}
          </div>
        </Cartao>
      )}
    </div>
  );
}

function Resumo({
  rotulo,
  valor,
  nota,
  destaque = false,
}: {
  rotulo: string;
  valor: string;
  nota?: string;
  destaque?: boolean;
}) {
  return (
    <div className="rounded border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">{rotulo}</p>
      <p
        className={
          destaque
            ? 'mt-1 font-mono text-2xl font-semibold text-slate-900'
            : 'mt-1 font-mono text-lg text-slate-900'
        }
      >
        {valor}
      </p>
      {nota !== undefined && <p className="mt-1 text-xs text-slate-500">{nota}</p>}
    </div>
  );
}
